import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Props from './CartInventoryComponent';
import FormattedCurrency from '@s/FormattedCurrency';
import DetailsToggle from '@s/DetailsToggle';
import { getEventDate } from '@h/helpers';
import CartInventoryTable from './CartInventoryTable';
import CartInventoryTableProductRow from './CartInventoryTableProductRow';
import Translation from '../../../../shared/Translation';
import { TranslationContext } from '@c/Translations';
import { getMainEvent } from '../../../selectors/event';
import { connect } from 'react-redux';
import AddTickets from '@v/ticket-picker/components/AddTickets';

class CartInventory extends Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      showDescriptions: {}, // map of what descriptions are showing
      loadingValues: {}, // map of temp values to display while a request is loading to make UI more responsive
      loadingProductValues: {},
      loadingBundleValues: {}
    };
  }
  
  fireLoadingStatusChanged = (loading) => {
    this.props.onLoadingStatusChanged(loading);
  };  

  render() {
    if (!this.props.levels && !this.props.products && !this.props.bundles.length) {
      return null;
    }

    return (
      <section className="cart__items-per-event">
        <CartInventoryTable
          header={
            <Fragment>
              <h4 className="cart__event-info__name" aria-level="2">{this.props.eventName}</h4>
              <p className="cart__event-info__date">
                {getEventDate(this.props.eventStart, this.props.eventEnd, this.props.locale, this.props.hide_event_time, this.props.hide_event_date_and_time)}
              </p>
              <p className="cart__event-info__venue">
                {this.props.venue}
              </p>
            </Fragment>
          }
          body={
            <TranslationContext.Consumer>
              {({ t }) => (
              <Fragment>
                {this.renderPriceLevels(t)}
                {this.renderProducts(t)}
                {this.renderBundles(t)}
              </Fragment>
              )}
            </TranslationContext.Consumer>
          }
        />
      </section>
    );
  }

  levelIsLoading = level => {
    return (
      (!level.listing_upsell_id && this.props.loadingLevels.indexOf(level.price_level_id) !== -1) ||
      (level.listing_upsell_id &&
        this.props.loadingLevels.indexOf(`${level.listing_upsell_id}_${level.price_level_id}`) !== -1)
    );
  };

  renderPriceLevels = (t) => {
    if (!this.props.levels) {
      return null;
    }

    return this.props.levels.map((level, i) => {
      let loading = this.levelIsLoading(level);
      const isTimeslotBuyout = level.is_tsb;
      const levelQuantity = isTimeslotBuyout
        ? 1
        : level.quantity;
      this.fireLoadingStatusChanged(loading);

      const isGaLevel = level.rows?.length === 1 && level.rows[0].seatsio_ga_id;
      const feeToDisplay = isTimeslotBuyout ? level.total_fee : level.fee;
      const selectAriaLabel = t('checkout__quantity_for_item', { item_name: level.name });

      return (
        <div className={'cart-ticket' + (loading ? ' loading-state__dim' : '')} key={i}>
          <div className="cart-ticket__info">
            <div id={`name_cart-ticket_${level.id}`} className="cart-ticket__name" role='heading' aria-level='3'>{level.name}</div>

            {level.rows.map((row, subIndex) => !row.seatsio_ga_id && (
              <div className="cart-ticket__name" role='heading' aria-level='3' key={subIndex}>
                {this.getSeatDescription(row)}
              </div>
            ))}

            {feeToDisplay ? (
              <div className="cart-ticket__pricing-group">
                <div className="cart-ticket__price">
                  <FormattedCurrency>{level.price}</FormattedCurrency>
                </div>
                <div className="cart-ticket__fee">
                  + <FormattedCurrency>{feeToDisplay}</FormattedCurrency>
                  &nbsp;
                  <Translation>{this.props.feeVerbiage ? 'checkout__fees_label' : 'checkout__service_fee_label'}</Translation>
                </div>
              </div>
            ) : null}
            {this.shouldShowPriceLevelDescription(level) && (
              <div className="cart-ticket__description">
                <TranslationContext.Consumer>
                  {t => (
                    <DetailsToggle
                      moreToggle={isTimeslotBuyout ? t.t('checkout__see_contents') : undefined}
                      lessToggle={isTimeslotBuyout ? t.t('checkout__hide_contents') : undefined}
                      onClick={this.toggleDescription(level.level_id)}
                      aria-describedby={`name_cart-ticket_${level.id}`}
                      aria-expanded={this.state.showDescriptions[level.level_id]}
                      aria-controls={`description_cart-ticket_${level.id}`}
                    />
                  )}
                </TranslationContext.Consumer>
                {this.state.showDescriptions[level.level_id] === true && (
                  <div
                    id={`description_cart-ticket_${level.id}`}
                    className="cart-ticket__description-content"
                    name="description_{index}"
                    dangerouslySetInnerHTML={{
                      __html: this.getPriceLevelDescription(level)
                    }}
                  />
                )}
              </div>
            )}
          </div>
          <div className="cart-ticket__controls">
            <div className="cart-ticket__quantity">
              {(!level?.is_seated || isGaLevel) && (
                <div className="editable-quantity">
                  <AddTickets
                    quantities={isTimeslotBuyout ? [1] : level.quantities}
                    quantity={loading ? this.state.loadingValues[level.price_level_id] : levelQuantity}
                    disabled={loading || isTimeslotBuyout}
                    onIncrement={() => this.onLevelIncrement(level)}
                    onDecrement={() => this.onLevelDecrement(level)}
                    onQuantityInput={(e) => this.onLevelChanged(level, +e.target.value)}
                  />
                </div>
              )}
              {(level?.is_seated && !isGaLevel) && (
                <div
                  className="editable-quantity__input"
                  aria-label={`${t('checkout__quantity')}, ${level.name}`}
                >
                  {level?.quantity ?? ''}
                </div>
              )}
            </div>
            <div className="cart-ticket__action">
              <TranslationContext.Consumer>
                {({ t }) => (
                  <button
                    className="button--clear button--small"
                    disabled={this.props.hasDependentProducts && Object.keys(this.props.levels).length === 1}
                    onClick={this.clearLevel(level)}
                    aria-label={t('checkout__delete_quantity', {
                      quantity: level.quantity,
                      itemName: level.name
                    })}
                  >
                    <Translation>{'checkout__remove'}</Translation>
                  </button>
                )}
              </TranslationContext.Consumer>
            </div>
            <div className="cart-ticket__subtotal">
              <FormattedCurrency>{level.subtotal}</FormattedCurrency>
            </div>
          </div>
        </div>
      );
    });
  };

  shouldShowPriceLevelDescription = (level) => {
    const isTimeslotBuyout = !!level.is_tsb;
    return level.description || isTimeslotBuyout
  }

  getPriceLevelDescription = (level) => {
    const isTimeslotBuyout = !!level.is_tsb
    return isTimeslotBuyout ? this.getTimeslotBuyoutContent(level) : level.description
  }

  getTimeslotBuyoutContent = level => {
    const ticketCount = this.props.tickets.reduce(
      (count, ticket) => count + (+ticket.price_level_id === +level.price_level_id ? 1 : 0),
      0
    )
    return (
      <Translation
        interpolation={{
          quantity: ticketCount,
          itemName: level.name,
          description: ""
        }}
      >
        {"checkout__quantity_by_name_and_description"}
      </Translation>
    )
  };

  renderProducts = () => {
    if (!this.props.products) {
      return null;
    }
    return this.props.products.map((product, i) => {
      let loading = this.props.loadingProductConfig.indexOf(parseInt(product.configuration_id)) !== -1;
      this.fireLoadingStatusChanged(loading);      
      return (
        <CartInventoryTableProductRow
          key={i}
          product={product}
          loading={loading}
          onIncrement={() => this.onProductConfigChange(product, product.quantity + 1)}
          onDecrement={() => this.onProductConfigChange(product, product.quantity - 1)}
          onQuantityInput={(e) => this.onProductConfigChange(product, +e.target.value)}
          onRemove={this.removeProductConfiguration(product)}
          quantity={loading ? this.state.loadingProductValues[product.configuration_id] : product.quantity}
        />
      );
    });
  };

  renderBundles = (t) => {
    if (!this.props.bundles.length) {
      return null;
    }

    return this.props.bundles.map((bundleRow, i) => {
      let bundle = this.getBundleFromRow(bundleRow);
      let loading = this.props.loadingBundles.indexOf(parseInt(bundle.bundle_id)) !== -1;
      this.fireLoadingStatusChanged(loading);      
      const selectAriaLabel = t('checkout__quantity_for_item', { item_name: bundle.name });
      return (
        <div className={'cart-ticket' + (loading ? ' loading-state__dim' : '')} key={i}>
          <div className="cart-ticket__info">
            <div id={`name_cart-bundle_${bundle.id}`} className="cart-ticket__name" role='heading' aria-level='3'>{bundle.name}</div>
            {bundle.fee ? (
              <div className="cart-ticket__pricing-group">
                <div className="cart-ticket__price">
                  <FormattedCurrency>{bundle.cost}</FormattedCurrency>
                </div>
                <div className="cart-ticket__fee">
                  + <FormattedCurrency>{bundle.fee}</FormattedCurrency>
                  &nbsp;
                  <Translation>{this.props.feeVerbiage ? 'checkout__fees_label' : 'checkout__service_fee_label'}</Translation>
                </div>
              </div>
            ) : null}
            <div className="cart-ticket__description">
              <TranslationContext.Consumer>
                {t => (
                  <DetailsToggle
                    onClick={this.toggleDescription(bundle.bundle_sale)}
                    moreToggle={t.t('checkout__see_contents')}
                    lessToggle={t.t('checkout__hide_contents')}
                    aria-describedby={`name_cart-bundle_${bundle.id}`}
                    aria-expanded={this.state.showDescriptions[bundle.bundle_sale]}
                    aria-controls={`description_cart-bundle_${bundle.id}`}
                  />
                )}
              </TranslationContext.Consumer>

              {this.state.showDescriptions[bundle.bundle_sale] === true && (
                <div id={`description_cart-bundle_${bundle.id}`} className="cart-ticket__description-content" name="description_{index}">
                  {this.getBundleContent(bundle)}
                </div>
              )}
            </div>
          </div>
          <div className="cart-ticket__controls">
            <div className="cart-ticket__quantity">
              <div className="editable-quantity">
                <AddTickets
                  quantities={bundle.quantities}
                  quantity={loading ? this.state.loadingBundleValues[bundle.bundle_id] : bundle.quantity}
                  disabled={loading}
                  onIncrement={() => this.onBundleChanged(bundle, bundle.quantity + 1)}
                  onDecrement={() => this.onBundleChanged(bundle, bundle.quantity - 1)}
                  onQuantityInput={(e) => this.onBundleChanged(bundle, +e.target.value)}
                />
              </div>
            </div>
            <div className="cart-ticket__action">
              <TranslationContext.Consumer>
                {({ t }) => (
                  <button
                    className="button--clear button--small"
                    onClick={this.removeBundle(bundle)}
                    aria-label={t('checkout__delete_quantity', {
                      quantity: bundle.quantity,
                      itemName: bundle.name
                    })}
                  >
                    <Translation>{'checkout__remove'}</Translation>
                  </button>
                )}
              </TranslationContext.Consumer>
            </div>
            <div className="cart-ticket__subtotal">
              <FormattedCurrency>{bundle.subtotal}</FormattedCurrency>
            </div>
          </div>
        </div>
      );
    });
  };

  getBundleFromRow = bundleRow => {
    let groupedBundle = {};
    let bundleSaleIds = [];
    for (bundle in bundleRow) {
      bundleSaleIds.push(bundleRow[bundle].id);
    }
    let bundle = bundleRow[0];

    groupedBundle.bundle_id = bundle.bundle_id;
    groupedBundle.bundle_sale_ids = bundleSaleIds;
    groupedBundle.bundle_sale = bundle.id;
    groupedBundle.name = bundle.name;
    groupedBundle.description = bundle.description;
    groupedBundle.event_id = bundle.event_id;
    groupedBundle.quantity = bundleRow.length;
    groupedBundle.quantities = Array.isArray(bundle?.quantityOptions) ? bundle.quantityOptions : [];

    groupedBundle.cost = bundle.cost.amount;
    groupedBundle.total_cost = bundleRow.length * bundle.cost.amount;
    groupedBundle.fee = bundle.buyer_fee.amount;
    groupedBundle.total_fee = bundleRow.length * bundle.buyer_fee.amount;
    groupedBundle.subtotal = bundleRow.length * (bundle.cost.amount + bundle.buyer_fee.amount);
    groupedBundle.contents = bundle.contents;

    return groupedBundle;
  };

  getBundleContent = bundle => {
    return (
      <div className="bundle__content">
        <span className="bundle-includes">
          {this.getBundleProducts(bundle)}
          {this.getBundleTickets(bundle)}
        </span>
      </div>
    );
  };

  getBundleProducts = bundle => {
    let products = bundle.contents.products;
    let bundleProducts = [];
    Object.values(products).map(configuration => {
      Object.values(configuration).map((config, i) => {
        if (config.configuration_name !== '') {
          bundleProducts.push(
            <span key={'config_' + config.product_name + '_' + i} className="line-br">
              <Translation
                interpolation={{
                  quantity: config.quantity,
                  itemName: config.product_name,
                  configName: config.configuration_name
                }}
              >
                {'checkout__quantity_by_name_and_config'}
              </Translation>
              <br />
            </span>
          );
        } else {
          bundleProducts.push(
            <span key={'noConfig_' + config.product_name + '_' + i} className="line-br">
              <Translation interpolation={{ quantity: config.quantity, itemName: config.product_name }}>
                {'checkout__x_quantity'}
              </Translation>
              <br />
            </span>
          );
        }
      });
    });

    return bundleProducts;
  };

  getBundleTickets = bundle => {
    let tickets = bundle.contents.tickets;
    let bundleTickets = [];
    Object.values(tickets).map((ticket, i) => {
      let ticketDescription = '';

      if (this.props.isPackage && ticket.event) {
        ticketDescription += ' for ' + ticket.event;
      }

      bundleTickets.push(
        <span className="line-br" key={'pl_' + i}>
          <Translation
            interpolation={{ quantity: ticket.quantity, itemName: ticket.ticket, description: ticketDescription }}
          >
            {'checkout__quantity_by_name_and_description'}
          </Translation>
          <br />
        </span>
      );
    });

    return bundleTickets;
  };

  onProductConfigChange = (product, value) => {
    this.props.onProductSelectionChange(value, product.configuration_id);
    let values = this.state.loadingProductValues;
    values[product.configuration_id] = value;
    this.setState({ loadingProductValues: values });
  };

  removeProductConfiguration = product => {
    return e => {
      if (this.props.loadingProductConfig.indexOf(product.configuration_id) !== -1) {
        return;
      }

      this.props.onProductSelectionChange(0, product.configuration_id);
    };
  };

  onLevelIncrement = (level) => {
    if (level?.quantities?.length) {
      const currentQuantity = !!level.is_tsb ? 1 : level.quantity;
      const currentIndex = level.quantities.indexOf(currentQuantity);
      if (currentIndex < 0) {
        this.onLevelChanged(level, level.quantities[0])
      } else if (currentIndex < (level.quantities.length - 1)) {
        this.onLevelChanged(level, level.quantities[currentIndex + 1]);
      }
    }
  }

  onLevelDecrement = (level) => {
    if (level?.quantities?.length) {
      const currentQuantity = !!level.is_tsb ? 1 : level.quantity;
      const currentIndex = level.quantities.indexOf(currentQuantity);
      if (currentIndex === 0) {
        this.onLevelChanged(level, 0);
      } else if (currentIndex > -1) {
        this.onLevelChanged(level, level.quantities[currentIndex  - 1]);
      }
    }
  }

  onLevelChanged = (level, value) => {
    this.props.onSelectionChanged(value, level.price_level_id, level.listing_upsell_id);
    let values = this.state.loadingValues;
    values[level.price_level_id] = value;
    this.setState({ loadingValues: values });
  };

  onBundleChanged = (bundle, value) => {
    this.props.onBundleSelectionChange(value, bundle);
    let values = this.state.loadingBundleValues;
    values[bundle.bundle_id] = value;
    this.setState({ loadingBundleValues: values });
  };

  clearLevel = level => {
    return e => {
      // disable without using disabled prop, it makes the button ugly
      if (this.levelIsLoading(level)) {
        return;
      }

      this.props.onSelectionChanged(0, level.price_level_id, level.listing_upsell_id);
    };
  };

  removeBundle = bundle => {
    return e => {
      if (this.props.loadingBundles.indexOf(bundle.bundle_id) !== -1) {
        return;
      }

      this.props.onBundleSelectionChange(0, bundle);
    };
  };

  toggleDescription = id => {
    return () => {
      let show = { ...this.state.showDescriptions };
      if (!this.state.showDescriptions[id]) {
        show[id] = true;
      } else {
        delete show[id];
      }
      this.setState({ showDescriptions: show });
    };
  };

  getSeatDescription = row => {
      const placeType = row.isTableSeat ? 'Table' : 'Row';
      return (
        <>
          {row.section ? 'Sec ' : ''} {row.section ?? ''}
          {row.row ? (row.section ? `, ${placeType} ` : `${placeType} `) : ''} {row.row}
          {row.seats_info ? ', Seat(s) ' : ''} {row.seats_info}
        </>
      );
  }
}

CartInventory.propTypes = {
  locale: PropTypes.string.isRequired,
  levels: PropTypes.arrayOf(PropTypes.object),
  products: PropTypes.arrayOf(PropTypes.object),
  bundles: PropTypes.arrayOf(PropTypes.array),
  loadingLevels: PropTypes.array,
  loadingProductConfig: PropTypes.arrayOf(PropTypes.number),
  loadingBundles: PropTypes.arrayOf(PropTypes.number),
  clearReservation: PropTypes.func.isRequired,
  eventName: PropTypes.string,
  eventStart: PropTypes.string,
  eventEnd: PropTypes.string,
  onLoadingStatusChanged: PropTypes.func.isRequired,
  tickets: PropTypes.arrayOf(PropTypes.object),
};

const mapStateToProps = (state, props) => {
  return {
      tickets: state.tickets?.tickets || [],
      hide_event_time: state.ui.uiConfig.hide_event_time,
      hide_event_date_and_time: state.ui.uiConfig.hide_event_date_and_time,
      feeVerbiage: getMainEvent(state)?.event?.fee_verbiage,
  }
}

export default connect(mapStateToProps)(CartInventory)
